// ═══════════════════════════════════════════════════════════════
// ClearView CRM Contact Log Service — Supabase CRUD
// ═══════════════════════════════════════════════════════════════
import { supabase } from "../lib/supabase";
import { toCamelCase } from "../utils/fieldMapper";
import { debugLog } from "../utils/debugLog";

export const contactLogService = {
  /**
   * Add a contact log entry for a member
   */
  async create(memberId, entry) {
    const { data, error } = await supabase
      .from("contact_logs")
      .insert({
        member_id: memberId,
        type: entry.type || "email",
        direction: entry.direction || "outbound",
        subject: entry.subject || "",
        notes: entry.notes || "",
        purpose: entry.purpose && entry.purpose.length > 0 ? entry.purpose : null,
        log_date: entry.date || new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      debugLog.error("contactLogService.create", error);
      throw error;
    }
    debugLog.action("Contact log created", { memberId, type: entry.type });
    return toCamelCase(data);
  },

  /**
   * Update a contact log entry
   */
  async update(logId, updates) {
    const payload = {};
    if (updates.type !== undefined) payload.type = updates.type;
    if (updates.subject !== undefined) payload.subject = updates.subject;
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.purpose !== undefined) payload.purpose = updates.purpose;
    if (updates.direction !== undefined) payload.direction = updates.direction;

    const { data, error } = await supabase
      .from("contact_logs")
      .update(payload)
      .eq("id", logId)
      .select()
      .single();

    if (error) {
      debugLog.error("contactLogService.update", error);
      throw error;
    }
    debugLog.action("Contact log updated", { logId });
    return toCamelCase(data);
  },

  /**
   * Delete a contact log entry
   */
  async remove(logId) {
    const { error } = await supabase
      .from("contact_logs")
      .delete()
      .eq("id", logId);

    if (error) {
      debugLog.error("contactLogService.remove", error);
      throw error;
    }
    debugLog.action("Contact log deleted", { logId });
  },

  /**
   * Get all contact logs for a member
   */
  async getByMember(memberId) {
    const { data, error } = await supabase
      .from("contact_logs")
      .select("*")
      .eq("member_id", memberId)
      .order("log_date", { ascending: false });

    if (error) {
      debugLog.error("contactLogService.getByMember", error);
      throw error;
    }
    return data.map(toCamelCase);
  },

  /**
   * Latest contact-log date per member. Used by CapacityView's
   * "last touched" column — cheaper than pulling full log history
   * for every member in the portfolio.
   *
   * Returns { [memberId]: ISO timestamp }. Empty object on failure.
   */
  async getLatestDatesByMember() {
    try {
      // Pull just the two columns, ordered DESC. First occurrence
      // per member_id is the latest. Limit is a safety cap.
      const { data, error } = await supabase
        .from("contact_logs")
        .select("member_id, log_date")
        .order("log_date", { ascending: false })
        .limit(20000);
      if (error) throw error;
      const latest = {};
      (data || []).forEach((row) => {
        if (!latest[row.member_id]) {
          latest[row.member_id] = row.log_date;
        }
      });
      return latest;
    } catch (err) {
      debugLog.error("contactLogService.getLatestDatesByMember", err);
      return {};
    }
  },

  /**
   * Get all contact logs since a given date (for reports)
   */
  async getSince(sinceDate) {
    const { data, error } = await supabase
      .from("contact_logs")
      .select("*")
      .gte("log_date", sinceDate)
      .order("log_date", { ascending: false });

    if (error) {
      debugLog.error("contactLogService.getSince", error);
      throw error;
    }
    return data.map(toCamelCase);
  },
};
